<script setup lang="ts">
import DiagnoseButton from './DiagnoseButton.vue';
import { useMediaQuery } from '@vueuse/core';
import RatingStars from 'theme/components/molecules/RatingStars.vue';

const mustShowLessItem = useMediaQuery('(max-width: 1024px)');

type Review = { personality: string; score: number; comment: string };
const reviews = computed((): Review[] => [
  {
    personality: '女性・30歳',
    score: 5,
    comment:
      'ライフプラン表作成を依頼しました。将来をどうしたいか漠然としていましたが、とても丁寧に対応していただきました。ありがとうございました。',
  },
  {
    personality: '男性・33歳',
    score: 5,
    comment:
      'ヒアリングから作業(キャッシュフロー表)までも早く、その後のアドバイスについても、自分が漠然と考えていた改善点を言語化して説明してくれました。',
  },
  {
    personality: '女性・37歳',
    score: 5,
    comment:
      '丁寧にヒアリングしてくださり、家計が目に見えてとても勉強になりました。この流れで、これから頑張って知識を取り入れ行動していこうと思います。',
  },
  {
    personality: '女性・29歳',
    score: 5,
    comment:
      '現役子育て中のママさんなこともあり、経験や実感も交えた貴重なアドバイスをいただけました。',
  },
  {
    personality: '女性・30歳',
    score: 5,
    comment:
      'とても感じのいいプランナーさんで最初から最後まで有意義な時間になりました！色々悩んでいることもポジティブに変換してくださったので、心がスッキリもしました！！',
  },
  {
    personality: '女性・53歳',
    score: 5,
    comment:
      '資産運用や老後資金など、不安がありながらもどうしていいか分からず放置している問題が沢山ありました。 もっと早くこのような相談をさせていただいていればよかったです。',
  },
  {
    personality: '女性・32歳',
    score: 5,
    comment:
      '相談中に分からないところがないか逐次確認をしてくれたり、専門の知識を持ったスタッフの方に取り次いで予約を取ってくれたり全体がとてもスムーズに進んで気持ちよかったです',
  },
  {
    personality: '女性・33歳',
    score: 5,
    comment:
      'お金がないのは自分の使い方が悪いのだと思っていましたが、むしろ良く出来ていると言っていただけて嬉しかったです。',
  },
]);
</script>

<template>
  <div :class="$style.container">
    <p :class="$style.container__heading">体験した人の口コミ</p>
    <p :class="$style.container__subheading">Reviews</p>
    <Carousel
      :items-to-show="mustShowLessItem ? 1.2 : 2.4"
      :snap-align="mustShowLessItem ? 'center-odd' : 'center-even'"
      :autoplay="3000"
      :transition="800"
      pause-autoplay-on-hover
      wrap-around
      :class="$style.container__reviews"
    >
      <Slide v-for="(review, i) in reviews" :key="i">
        <div :class="$style.reviewCard">
          <p :class="$style.reviewCard__personality">
            {{ review.personality }}
          </p>
          <RatingStars
            :class="$style.reviewCard__stars"
            :rate="review.score"
            :show-rate-number="true"
          />
          <p :class="$style.reviewCard__comment">{{ review.comment }}</p>
        </div>
      </Slide>
    </Carousel>
    <div :class="[$style.container__plannerCard, $style.plannerCard]">
      <div :class="$style.plannerCard__container">
        <img :class="$style.plannerCard__img" src="./planner.png" />
        <div>
          <p :class="$style.plannerCard__heading">アドバイザーからのコメント</p>
          <p :class="$style.plannerCard__body">
            家計改善や資産運用、ライフプランニング、教育資金・老後資金の準備、保険、住宅ローン、相続、不動産投資など、幅広いご相談を受けることが可能です。ご相談は無料ですので、お気軽にご相談ください。
          </p>
        </div>
      </div>
    </div>
    <DiagnoseButton :class="$style.container__button" />
  </div>
</template>

<style module lang="scss">
.container {
  padding: 80px 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  @include mobile-only {
    padding: 40px 16px;
  }

  &__heading {
    font-size: 40px;
    font-weight: 600;
    @include mobile-only {
      font-size: 28px;
      font-weight: 600;
    }
  }

  &__subheading {
    margin-top: 8px;
    color: $color-text-and-object-accent-primary;
    font-size: 16px;
    font-weight: 600;
  }

  &__reviews {
    margin-top: 40px;
    width: 100%;
    max-width: 1024px;
  }

  &__plannerCard {
    margin-top: 20px;
  }

  &__button {
    margin-top: 40px;
  }
}

.reviewCard {
  text-align: start;
  width: 410px;
  height: 240px;
  padding: 24px;
  border-radius: 16px;
  box-shadow: $shadow-8;
  margin: 12px;

  &__stars {
    font-size: 18px;
  }

  &__personality {
    font-size: 16px;
    font-weight: 600;
  }

  &__comment {
    margin-top: 12px;
    font-size: 20px;
    font-weight: 600;
    @media only screen and (max-width: 1024px) {
      font-size: 14px;
    }
  }
}

.plannerCard {
  background-color: #f2f3f4;
  max-width: 1024px;
  margin-left: 16px;
  margin-right: 16px;
  padding: 24px;
  border-radius: 16px;
  @include create-stacking-context;
  position: relative;

  &__img {
    width: 120px;
    height: 120px;
  }

  &__heading {
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
  }

  &__body {
    font-size: 14px;
    line-height: 20px;
    margin-top: 4px;
  }

  &__container {
    align-items: center;
    display: flex;
    gap: 24px;
    @include mobile-only {
      flex-direction: column;
    }
  }
}
</style>
